<script setup lang="ts">
// The form for adding a storage location: an S3 bucket, or (admins, with a root the
// configuration allows) a folder on the server. Used by SRV → Attachments and by members'
// own storage in Settings (docs/STORAGE.md §2.5).
import { computed, reactive, ref, watch } from 'vue'
import { describeError } from '../api/http'
import type { AddStorageLocation, StorageKind, StorageLocation } from '../api/types'
import { useSession } from '../stores/session'

const props = withDefaults(
  defineProps<{
    /** Adds the location (checking it first) and returns it. */
    submit: (req: AddStorageLocation) => Promise<StorageLocation>
    /** Folders admins may add locations under; none: buckets only. */
    diskRoots?: string[]
    /** Offer folders at all (admins); members attach buckets only. */
    folders?: boolean
  }>(),
  { diskRoots: () => [], folders: true },
)
const emit = defineEmits<{ added: [location: StorageLocation]; cancel: [] }>()

const session = useSession()
const busy = ref(false)
const blank = (): AddStorageLocation => ({
  name: '',
  kind: 's3',
  path: '',
  endpoint: '',
  region: '',
  bucket: '',
  prefix: '',
  access_key_id: '',
  secret_access_key: '',
})
const form = reactive<AddStorageLocation>(blank())
const kinds: { value: StorageKind; label: string }[] = [
  { value: 's3', label: 'S3 bucket' },
  { value: 'disk', label: 'Folder on this server' },
]

const canAddDisk = computed(() => props.diskRoots.length > 0)
// Folders must be inside a root, so start the field there.
watch(
  () => form.kind,
  (k) => {
    if (k === 'disk' && !form.path) form.path = `${props.diskRoots[0] ?? ''}/`
  },
)
const ready = computed(() =>
  form.name.trim() && (form.kind === 'disk' ? form.path?.trim() : form.bucket?.trim() && form.access_key_id?.trim() && form.secret_access_key?.trim()),
)

async function add(): Promise<void> {
  const req: AddStorageLocation = { name: form.name.trim(), kind: form.kind }
  if (form.kind === 'disk') req.path = form.path?.trim()
  else
    Object.assign(req, {
      endpoint: form.endpoint?.trim() || undefined,
      region: form.region?.trim() || undefined,
      bucket: form.bucket?.trim(),
      prefix: form.prefix?.trim() || undefined,
      access_key_id: form.access_key_id?.trim(),
      secret_access_key: form.secret_access_key,
    })
  busy.value = true
  try {
    const added = await props.submit(req)
    Object.assign(form, blank())
    emit('added', added)
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <form class="mt-4 grid gap-3 border border-border p-4 md:grid-cols-2" data-testid="storage-add" @submit.prevent="add">
    <div v-if="folders" class="space-y-1.5 md:col-span-2" role="radiogroup" aria-label="Kind of location">
      <label v-for="k in kinds" :key="k.value" class="mr-4 inline-flex cursor-pointer items-center gap-2 text-sm">
        <input v-model="form.kind" type="radio" name="storage-kind" :value="k.value" :disabled="k.value === 'disk' && !canAddDisk" />
        {{ k.label }}
      </label>
      <p v-if="!canAddDisk" class="mono text-text-muted">Folders can only be added in the server's configuration (--storage, --storage-root).</p>
    </div>
    <div>
      <label class="label text-text-muted" for="storage-name">Name</label>
      <input id="storage-name" v-model="form.name" class="input" maxlength="48" data-testid="storage-name" />
    </div>
    <template v-if="form.kind === 'disk'">
      <div>
        <label class="label text-text-muted" for="storage-path">Folder inside {{ diskRoots.join(' or ') }}</label>
        <input id="storage-path" v-model="form.path" class="input" :placeholder="`${diskRoots[0]}/team`" data-testid="storage-path" />
        <p class="mono mt-1 text-text-muted">A full path, or just a name for a folder inside {{ diskRoots[0] }}. It's created if it doesn't exist.</p>
      </div>
    </template>
    <template v-else>
      <div>
        <label class="label text-text-muted" for="storage-endpoint">Endpoint</label>
        <input id="storage-endpoint" v-model="form.endpoint" class="input" placeholder="https://s3.example.com (empty for AWS)" data-testid="storage-endpoint" />
      </div>
      <div>
        <label class="label text-text-muted" for="storage-bucket">Bucket</label>
        <input id="storage-bucket" v-model="form.bucket" class="input" data-testid="storage-bucket" />
      </div>
      <div>
        <label class="label text-text-muted" for="storage-prefix">Folder in the bucket (optional)</label>
        <input id="storage-prefix" v-model="form.prefix" class="input" data-testid="storage-prefix" />
      </div>
      <div>
        <label class="label text-text-muted" for="storage-region">Region (optional)</label>
        <input id="storage-region" v-model="form.region" class="input" placeholder="us-east-1" data-testid="storage-region" />
      </div>
      <div>
        <label class="label text-text-muted" for="storage-key-id">Access key ID</label>
        <input id="storage-key-id" v-model="form.access_key_id" class="input" autocomplete="off" data-testid="storage-key-id" />
      </div>
      <div>
        <label class="label text-text-muted" for="storage-secret">Secret access key</label>
        <input id="storage-secret" v-model="form.secret_access_key" type="password" class="input" autocomplete="off" data-testid="storage-secret" />
      </div>
      <p class="mono text-text-muted md:col-span-2">
        Use a key that can only reach this bucket. It's stored encrypted on this server, which uses it to let people
        upload and play files there. Revoke it with your provider to cut the server off.
      </p>
    </template>
    <div class="flex justify-end gap-2 md:col-span-2">
      <button type="button" class="btn-ghost" @click="emit('cancel')">Cancel</button>
      <button class="btn-primary" :disabled="busy || !ready" data-testid="storage-add-save">{{ busy ? "Checking… (up to 20 s)" : "Check and add" }}</button>
    </div>
  </form>
</template>
